import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  ProductShelvingRequirementInput,
  UpdateProductShelvingRequirementMutation,
  UpdateProductShelvingRequirementMutationVariables,
} from "@/graphql/codegen/graphql"
import { UpdateProductShelvingRequirementDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseUpdateProductShelvingRequirementReturn {
  update: (input: ProductShelvingRequirementInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export interface UseUpdateProductShelvingRequirementProps {
  errorMessage?: string
  successMessage?: string
  onSuccess: () => void
}

export const useUpdateProductShelvingRequirement = ({
  errorMessage = "Failed to update product shelving requirements.",
  successMessage = "Product shelving requirements successfully updated.",
  onSuccess,
}: UseUpdateProductShelvingRequirementProps): UseUpdateProductShelvingRequirementReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateProductShelvingRequirementMutation,
    UpdateProductShelvingRequirementMutationVariables
  >(UpdateProductShelvingRequirementDocument)

  const update = async (input: ProductShelvingRequirementInput) => {
    const { data, error } = await executeMutation({ input })

    if (error) {
      notify({
        type: NotificationType.Error,
        message: error.message ?? errorMessage,
      })
      return
    }

    if (data?.productShelvingRequirement.update) {
      notify({
        type: NotificationType.Success,
        content: successMessage,
      })
      onSuccess()
    }
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
